/*
 * Dependent-lookup staleness guard.
 *
 * A lookup field scoped by `lookupFilterValueFrom` (Jobs → Contact Person is
 * scoped to the selected Client) must not keep a value that was chosen under a
 * DIFFERENT parent.
 *
 * Changing the parent already refetches the OPTIONS, but the field's own VALUE
 * used to survive that: pick client A's contact "Naveen", switch to client B,
 * and A's contact id was still sitting on the form. It went unnoticed because a
 * nested reference id is only unique WITHIN its parent —
 * `clientContact.clientContactId` is a per-client sequence (1, 2, 3 …) — so the
 * orphaned id still resolved against the new client, to a DIFFERENT person, and
 * saved looking perfectly valid. A wrong name that looks right is worse than a
 * blank one.
 *
 * Kept as a pure function so the rule can be tested without rendering
 * AddFormV1/EditFormV1 (whose render pulls in pdfjs → DOMMatrix, which jsdom
 * does not have).
 */

// "This dependent has not observed its parent yet", so the FIRST value seen
// never counts as a change. undefined/null cannot serve as the sentinel: an
// unselected parent is legitimately undefined on mount.
export const PARENT_FILTER_UNSEEN = Symbol('parentFilterUnseen');

const empty = (v) => v === undefined || v === null || v === '';

// Filter values arrive as ObjectId hex strings, numbers, or boxed objects
// depending on the source, so compare by string form — otherwise a re-render
// that merely reboxes the same id would read as a parent change and wipe a
// perfectly valid choice.
export const sameFilterValue = (a, b) => String(a ?? '') === String(b ?? '');

/**
 * Should a dependent lookup drop its current value?
 *
 * @param {object}  args
 * @param {*}       args.previous      last observed parent value, or PARENT_FILTER_UNSEEN
 * @param {*}       args.next          the parent value now
 * @param {*}       args.currentValue  what the dependent field currently holds
 * @param {boolean} args.scoped        field has lookupFilterField + lookupFilterValueFrom
 */
export function shouldClearDependentValue({ previous, next, currentValue, scoped = true }) {
  // Not a scoped lookup — it has no parent, so nothing can invalidate it.
  if (!scoped) return false;
  // First observation: record, never clear. This is what keeps EDIT PREFILL
  // (which sets parent and child in the same pass) from wiping itself.
  if (previous === PARENT_FILTER_UNSEEN) return false;
  if (sameFilterValue(previous, next)) return false;
  // Parent only just became known — nothing was selectable under it before, so
  // whatever is in the field came from prefill, not from the old parent.
  if (empty(previous)) return false;
  // Nothing to clear.
  if (empty(currentValue)) return false;
  return true;
}
